import { FunctionComponent, RefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import { ProgressSpinner } from "primereact/progressspinner";
import { classNames } from "primereact/utils";

import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { TextMarkdown } from "../../../../components/Shared/Components";
import { TooltipWrapper } from "../../../../components/Shared/Components/TooltipWrapper/TooltipWrapper";
import { confirmActionPopup } from "../../../../components/Shared/Functions/ConfirmActionPopupFunction";
import { translationKeys } from "../../../../models/app-translation-keys";
import { useDisclaimerService } from "../../../../services/ServiceHooks/disclaimerHook";
import { useStore } from "../../../../stores/StoreContext";
import CheckboxDoNotShowAgain, { CheckboxHandle } from "./PopupDoNotShowAgain/CheckboxDoNotShowAgain"; // adjust path

import "./Disclaimer.scss";

type IDisclaimerType = "popup" | "text" | "tooltip";

function getWhitelabelBrandColor(): string {
  return getComputedStyle(document.documentElement).getPropertyValue("--customer-brand-color-background")?.trim();
}

const spinnerDefaultColor = getWhitelabelBrandColor();

interface IDisclaimerProps {
  type?: IDisclaimerType;
  fallbackText: string;
  doNotShowAgainKey?: string; // sessionStorage key for "don't show again" behavior
  focusTextArea: () => void;
}

const webSearchTranslationKeys = translationKeys.feature.chatbot.webSearch;
const tooltipTranslationKeys = translationKeys.feature.chatbot.tooltips;

const DisclaimerComponent: FunctionComponent<IDisclaimerProps> = ({
  type,
  fallbackText,
  doNotShowAgainKey,
  focusTextArea,
}) => {
  const { t } = useTranslation();
  const { chatStore, userStore } = useStore();

  const disclaimerService = useDisclaimerService();

  const toggleWebSearchButtonRef = useRef<HTMLDivElement>(null);
  const checkboxIdRef = useRef(`dontShowAgain_${Math.random().toString(36).slice(2)}`);
  const popupCheckboxRef = useRef<CheckboxHandle | null>(null);

  const [text, setText] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  const doNotShowAgainStatus = (): boolean =>
    !!((doNotShowAgainKey && sessionStorage.getItem(doNotShowAgainKey)) || "");

  // build the final message node by appending the checkbox if requested
  const finalMessageNode = (
    <div>
      <h2>{t(webSearchTranslationKeys.disclaimer.title)}</h2>

      <TextMarkdown>{text ?? fallbackText}</TextMarkdown>

      {doNotShowAgainKey && (
        <CheckboxDoNotShowAgain
          ref={popupCheckboxRef}
          inputId={checkboxIdRef.current}
          checkedByDefault={doNotShowAgainStatus()}
          label={t(webSearchTranslationKeys.actions.doNotShowAgain)}
        />
      )}
    </div>
  );

  // TODO: #55591 [FE] display Disclaimer according to its type ("popup" | "text" | "tooltip")
  // Task 56760 Make disclaimer configurable in admin-area with markdown editor
  const confirmStartWebSearch = async (target: RefObject<any>): Promise<boolean> => {
    return confirmActionPopup(t, {
      target: target,
      messageNode: finalMessageNode,
      defaultFocus: "accept",
      rejectLabel: t(translationKeys.actions.cancel),
      acceptIcon: <FontAwesomeIcon icon={faArrowRight} />,
      acceptLabel: t(webSearchTranslationKeys.actions.startWebSearch),
      acceptClassName: "inline-flex align-items-center gap-2 icon-button-reverse",
      style: { maxWidth: "450px" },
      acceptAction: () => {
        // Handle "do not show again" checkbox
        if (doNotShowAgainKey) {
          const checked = popupCheckboxRef.current?.isChecked() ?? false;
          if (checked) {
            sessionStorage.setItem(doNotShowAgainKey, "true");
          } else {
            sessionStorage.removeItem(doNotShowAgainKey);
          }
        }
      },
    });
  };

  const tryToggleWebSearch = async () => {
    if (chatStore.isWebSearchActive) {
      chatStore.setIsWebSearchActive(false);
      return;
    }

    // start web search when the "Do not show again" is checked
    // or when the "Do not show again" is not checked and user confirms the popup
    if (!!doNotShowAgainStatus() || (await confirmStartWebSearch(toggleWebSearchButtonRef))) {
      chatStore.setIsWebSearchActive(true);
      focusTextArea();
    }
  };

  const loadTranslationByLanguage = async () => {
    setLoading(true);
    try {
      const translatedText = await disclaimerService.getDisclaimerText(userStore.currentLanguage.code, "bing");
      setText(translatedText.text);
    } catch (error: any) {
      setText(undefined);

      if (error.response.status !== 404) {
        console.error("Failed to load disclaimer texts", error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTranslationByLanguage();
  }, [userStore.currentLanguage.code]);

  const webSearchButton = () => {
    return (
      <div
        className={classNames("toggle-web-search-button", {
          active: chatStore.isWebSearchActive,
          disabled: loading,
        })}
        onClick={loading ? undefined : tryToggleWebSearch}
        onKeyDown={event => event.key === "Enter" && (loading ? undefined : tryToggleWebSearch())}
        tabIndex={0}
        role="button"
        ref={toggleWebSearchButtonRef}
      >
        <TooltipWrapper
          targetRef={toggleWebSearchButtonRef}
          position="top"
          content={t(
            chatStore.isWebSearchActive
              ? tooltipTranslationKeys.webSearchActive
              : tooltipTranslationKeys.webSearchInActive,
          )}
        />
        Web
      </div>
    );
  };

  return loading ? (
    <ProgressSpinner className="small-medium-inline-spinner" color={spinnerDefaultColor} />
  ) : (
    webSearchButton()
  );
};

export const Disclaimer = observer(DisclaimerComponent);
